import React from 'react';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TokenPack, TokenPackPrice } from '../../types';
import { pickLocalized } from '../../utils/planChange';

interface TokenPackSectionProps {
  tokenPacks: TokenPack[];
  selectedCurrency: 'USD' | 'VND';
  onSelect: (tokenPack: TokenPack, price: TokenPackPrice) => void;
  formatPrice: (amountMinor: string | number, currency: string) => string;
}

const blockedMessages = {
  daily_limit: 'Daily purchase limit reached. Try again tomorrow.',
  monthly_limit: 'Monthly purchase limit reached.',
};

export const TokenPackSection: React.FC<TokenPackSectionProps> = ({
  tokenPacks,
  selectedCurrency,
  onSelect,
  formatPrice,
}) => {
  const { t, i18n } = useTranslation();

  if (tokenPacks.length === 0) {
    return null;
  }

  return (
    <div className="mb-12">
      <h2 className="mb-6 text-2xl font-bold text-gray-900 dark:text-slate-100">
        {t('subscription.advanceBalance.title', 'Advance Balance')}
      </h2>
      <p className="mb-6 max-w-3xl text-sm text-gray-600 dark:text-slate-400">
        {t('subscription.advanceBalance.explainer')}
      </p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {tokenPacks.map((tokenPack) => {
          const price =
            tokenPack.prices.find(
              (candidate) => candidate.currency === selectedCurrency && candidate.isActive !== false
            ) ||
            tokenPack.prices.find((candidate) => candidate.isActive !== false) ||
            tokenPack.prices[0];
          const blockedMessage = tokenPack.blockedReason
            ? blockedMessages[tokenPack.blockedReason]
            : null;
          const description = pickLocalized(tokenPack.description, i18n.language);

          if (!price) {
            return null;
          }

          return (
            <div
              key={tokenPack.publicId}
              className="rounded-lg border border-gray-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800"
            >
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                  {tokenPack.name}
                </h3>
                {description && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">{description}</p>
                )}
                {(tokenPack.estimates ?? []).length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                      {t('subscription.advanceBalance.estimateTitle', 'Roughly enough for')}
                    </p>
                    <ul className="mt-1 space-y-1">
                      {(tokenPack.estimates ?? []).map((estimate) => (
                        <li
                          key={estimate.code}
                          className="text-sm font-semibold text-blue-600 dark:text-blue-400"
                        >
                          ≈ {estimate.count.toLocaleString(i18n.language)}{' '}
                          {pickLocalized(estimate.label, i18n.language)}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                      {t(
                        'subscription.advanceBalance.estimateDisclaimer',
                        'Estimate based on average recent usage; actual usage varies by model and task.'
                      )}
                    </p>
                  </div>
                )}
                <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                  {t(
                    'subscription.advanceBalance.neverExpires',
                    'Does not expire when your billing cycle ends'
                  )}
                </p>
              </div>
              {blockedMessage && (
                <div className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                  {t(`subscription.advanceBalance.${tokenPack.blockedReason}`, blockedMessage)}
                </div>
              )}
              <Button
                variant="outlined"
                fullWidth
                disabled={!tokenPack.isPurchasable}
                onClick={() => onSelect(tokenPack, price)}
              >
                {t('subscription.advanceBalance.buy', {
                  defaultValue: 'Buy {{price}}',
                  price: formatPrice(price.amountMinor, price.currency),
                })}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
